import {
    ActivityProduct,
    ActivityTicket,
    CancellationPolicy,
    HotelProduct,
    ProductType,
    Room,
} from '@/api/Cart/cart.interfaces';
import {
    convertToDate,
    getDateAddingDuration,
    getDurationInHours,
    isAfter,
} from '@shared/helpers/datetime/datetime';
import { MainProduct } from '@shared/interfaces/product';
import { compare, onDate } from '@shared/utils/comparisons';
import { isEmpty } from 'lodash';
import {
    CartCancellationItem,
    CartItemCancellationPolicy,
    CartProductCancellation,
} from './CartCancellation.interfaces';
import { handleCustomError } from '@/config/errorHandling';
import { t } from '@shared/helpers/translation';
import { LabelFormat } from '@shared/interfaces/translation';

function getToday() {
    return getDateAddingDuration(new Date(), { days: 0 });
}

export function getCartProductCancellation(product: MainProduct): CartProductCancellation {
    const items: CartCancellationItem[] = getCartCancellationItems(product);

    return {
        Refundable: !isEmpty(items) && items.every((x) => x.Refundable),
        Items: items,
    };
}

function getCartCancellationItems(product: MainProduct): CartCancellationItem[] {
    switch (product.Type) {
        case ProductType.Hotel:
            return getHotelItems(product as HotelProduct);
        case ProductType.Activity:
            return getActivityItems(product as ActivityProduct);
        default:
            return [];
    }
}

function getHotelItems(product: HotelProduct): CartCancellationItem[] {
    const result: CartCancellationItem[] = [];
    product.Rooms.forEach((room, index) => {
        const roomPolicies = getRoomPolicies(room);
        result.push({
            Refundable:
                roomPolicies.length > 0
                    ? roomPolicies[0].Refundable && roomPolicies[0].Active
                    : undefined,
            ItemId: String(room.Id),
            Description: room.Description,
            Title: t({
                key: 'shared.room',
                suffix: ` ${index + 1}`,
                format: LabelFormat.Capitalize,
            }),
            Policies: roomPolicies,
        });
    });
    return result;
}

function getActivityItems(product: ActivityProduct): CartCancellationItem[] {
    const result: CartCancellationItem[] = [];
    product.Tickets.forEach((ticket, index) => {
        const ticketPolicies = getTicketPolicies(ticket);
        result.push({
            Refundable:
                ticketPolicies.length > 0
                    ? ticketPolicies[0].Refundable && ticketPolicies[0].Active
                    : undefined,
            ItemId: String(ticket.Id),
            Description: ticket.Description,
            Title: t({
                key: 'shared.ticket',
                suffix: ` ${index + 1}`,
                format: LabelFormat.Capitalize,
            }),
            Policies: ticketPolicies,
        });
    });
    return result;
}

export function getRoomPolicies(room: Room): CartItemCancellationPolicy[] {
    const mappedPolicies = mapPolicies(room.CustomerCancellationPolicies, room.Id);

    if (isEmpty(mappedPolicies)) return [];

    const zeroAmountPolicy = addZeroAmountPolicy(mappedPolicies);
    if (zeroAmountPolicy) mappedPolicies.unshift(zeroAmountPolicy);

    return mappedPolicies;
}

function getTicketPolicies(ticket: ActivityTicket): CartItemCancellationPolicy[] {
    const mappedPolicies = mapPolicies(ticket.CustomerCancellationPolicies, ticket.Id);

    if (isEmpty(mappedPolicies)) return [];

    const zeroAmountPolicy = addZeroAmountPolicy(mappedPolicies);
    if (zeroAmountPolicy) mappedPolicies.unshift(zeroAmountPolicy);

    return mappedPolicies;
}

function addZeroAmountPolicy(
    policies: CartItemCancellationPolicy[]
): CartItemCancellationPolicy | undefined {
    if (isEmpty(policies)) return undefined;
    const first = policies[0];
    if (first.Amount.Amount > 0) {
        const today = getToday();
        if (isAfter(today, convertToDate(first.FromDate))) return undefined;

        const hours = getDurationInHours(convertToDate(first.FromDate), today, false);
        return {
            Id: first.Id,
            Refundable: true,
            Active: hours > 0,
            Amount: {
                Amount: 0,
                Currency: first.Amount.Currency,
            },
            FromDate: today,
            EndDate: first.FromDate,
        };
    }
    return undefined;
}

export function getFirstAmountCartPolicy(
    cancellationProduct: CartProductCancellation
): CartItemCancellationPolicy | undefined {
    const policies: CartItemCancellationPolicy[] = cancellationProduct.Items.flatMap(
        (item) => item.Policies
    ).filter((x) => x.Refundable && x.Active);
    return sortCartCancellationPolicies(policies)[0];
}

function mapPolicies(
    policies: CancellationPolicy[],
    id: string
): CartItemCancellationPolicy[] {
    const sortedPolicies = sortCartPolicies(policies);

    if (!id) {
        handleCustomError(`No item id found while calculating cancellation policies.`);
        return [];
    } else if (isEmpty(sortedPolicies)) {
        handleCustomError(`No cancellation policies found for product ID ${id}`);
        return [];
    }

    return sortedPolicies.map((policy: CancellationPolicy, index) => {
        const today = getToday();
        const nextPolicy = index < policies.length ? policies[index + 1] : undefined;
        const endDate = nextPolicy ? nextPolicy.CancellationTime : undefined;
        const active = endDate
            ? getDurationInHours(convertToDate(endDate), today, false) > 0
            : true;

        return {
            Id: id,
            Refundable: policy.Amount.Amount === 0,
            FromDate: policy.CancellationTime,
            Active: active,
            EndDate: endDate,
            Amount: {
                Amount: policy.Amount.Amount,
                Currency: policy.Amount.Currency,
            },
        };
    });
}

function sortCartCancellationPolicies(
    policies: CartItemCancellationPolicy[]
): CartItemCancellationPolicy[] {
    if (policies.length === 0) return policies;

    const dateComparer = compare<CartItemCancellationPolicy>(
        onDate((policy) => convertToDate(policy.FromDate))
    );

    return policies.sort(dateComparer);
}

function sortCartPolicies(policies: CancellationPolicy[]): CancellationPolicy[] {
    if (policies.length === 0) return policies;

    const dateComparer = compare<CancellationPolicy>(
        onDate((policy) => convertToDate((policy as CancellationPolicy).CancellationTime))
    );

    return policies.sort(dateComparer);
}
